import {
  Button,
  Checkbox,
  DropdownMenu,
  Input,
  InputDecorator,
  Skeleton,
} from '@common/components';
import { SvgChevrondown, SvgRefresh } from '@common/icon';
import { cn } from '@core/utils';
import { useQuery } from '@tanstack/react-query';
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import CountUp from 'react-countup';

import { TERM_CATEGORY, UnauthQueryOptions } from '@/api';
import { BREAKPOINTS } from '@/constants/breakpoints';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useGlobalStore } from '@/stores/GlobalStore';

import { DEFAULT_DONATION_AMOUNT, MIN_DONATION_AMOUNT } from './constants';

interface PrefillFormProps {
  institutionId: number;
  defaultDonationBusinessId?: number;
  onSubmit: (data: { amount: number; purpose: string }) => void;
}

export const PrefillForm = ({
  institutionId,
  defaultDonationBusinessId,
  onSubmit,
}: PrefillFormProps) => {
  const isDesktopViewport = useMediaQuery(BREAKPOINTS.DESKTOP, true);

  const [agreements, setAgreements] = useState({
    welloTerms: false,
    privacyCollection: false,
    thirdPartyInfo: false,
    membershipTerms: false,
    addressVerification: false,
    shippingInfo: false,
    voluntaryDonation: false,
    refundPolicy: false,
    giftInfo: false,
  });

  const [isThirdPartyExpanded, setIsThirdPartyExpanded] = useState(false);

  const setTermsDrawerType = useGlobalStore(
    (state) => state.setTermsDrawerType,
  );

  const handleTermsView = (termType: TERM_CATEGORY) => {
    setTermsDrawerType(termType);
  };

  const requiredAgreements = [
    'welloTerms',
    'privacyCollection',
    'thirdPartyInfo',
    'membershipTerms',
    'addressVerification',
    'shippingInfo',
    'voluntaryDonation',
    'refundPolicy',
    'giftInfo',
  ] as const;

  const thirdPartySubItems = [
    'membershipTerms',
    'addressVerification',
    'shippingInfo',
  ] as const;

  const isAgreementAll = requiredAgreements.every((key) => agreements[key]);

  const isThirdPartyAllChecked = thirdPartySubItems.every(
    (key) => agreements[key],
  );

  const toggleAllAgreements = () => {
    const newValue = !isAgreementAll;
    setAgreements({
      welloTerms: newValue,
      privacyCollection: newValue,
      thirdPartyInfo: newValue,
      membershipTerms: newValue,
      addressVerification: newValue,
      shippingInfo: newValue,
      voluntaryDonation: newValue,
      refundPolicy: newValue,
      giftInfo: newValue,
    });
  };

  const updateAgreement = (key: keyof typeof agreements, checked: boolean) => {
    const newAgreements = {
      ...agreements,
      [key]: checked,
    };

    if (key === 'thirdPartyInfo') {
      thirdPartySubItems.forEach((subKey) => {
        newAgreements[subKey] = checked;
      });
    }
    if (
      thirdPartySubItems.includes(key as (typeof thirdPartySubItems)[number])
    ) {
      const allSubItemsChecked = thirdPartySubItems.every((subKey) =>
        subKey === key ? checked : newAgreements[subKey],
      );
      newAgreements.thirdPartyInfo = allSubItemsChecked;
    }

    setAgreements(newAgreements);
  };

  const toggleThirdPartyAccordion = () => {
    setIsThirdPartyExpanded(!isThirdPartyExpanded);
  };

  const [donation, setDonation] = useState({
    amount: DEFAULT_DONATION_AMOUNT,
    purpose: '',
  });

  const { data: donationBusinessPurposes } = useQuery({
    ...UnauthQueryOptions[
      'get:/wello/v2/allows/home/donation-business/institution/{institutionId}'
    ]({
      path: {
        institutionId,
      },
    }),
    select: ({ context }) => context?.contents,
  });

  useEffect(() => {
    if (donationBusinessPurposes && donationBusinessPurposes.length > 0) {
      const selectedPurpose =
        donationBusinessPurposes.find(
          (purpose) => purpose.donationBusinessId === defaultDonationBusinessId,
        ) ?? donationBusinessPurposes[0];

      setDonation((prev) => ({
        ...prev,
        purpose: selectedPurpose.donationBusinessName,
      }));
    }
  }, [donationBusinessPurposes, defaultDonationBusinessId]);

  const handleAmountChange = (value: string) => {
    const sanitizedValue = value.replaceAll(/[^0-9]/g, '');
    if (!/^[\d,]*$/.test(sanitizedValue)) return;

    const numberValue = Number(sanitizedValue);

    setDonation((prev) => ({
      ...prev,
      amount: numberValue,
    }));
  };

  const handleReset = () => {
    setDonation({
      amount: DEFAULT_DONATION_AMOUNT,
      purpose: donationBusinessPurposes?.[0]?.donationBusinessName ?? '',
    });

    setAgreements({
      welloTerms: false,
      privacyCollection: false,
      thirdPartyInfo: false,
      membershipTerms: false,
      addressVerification: false,
      shippingInfo: false,
      voluntaryDonation: false,
      refundPolicy: false,
      giftInfo: false,
    });
  };

  return (
    <div>
      <Input
        dimensions={isDesktopViewport ? 'H56' : 'H48'}
        placeholder="0"
        inputMode="numeric"
        value={donation.amount.toLocaleString()}
        onChange={(e) => handleAmountChange(e.target.value)}
        rightContent={
          <span className="font-400 text-Gray-900 typo-body3">원</span>
        }
        maxLength={16}
        className="mb-24"
      />
      <InputDecorator label="기부용도 선택">
        {donationBusinessPurposes ? (
          <DropdownMenu
            dimensions={isDesktopViewport ? 'H56' : 'H48'}
            options={donationBusinessPurposes.map(
              ({ donationBusinessName: donation_business_name }) => ({
                label: donation_business_name,
                value: donation_business_name,
              }),
            )}
            value={donation.purpose}
            onChange={(value) =>
              setDonation((prev) => ({
                ...prev,
                purpose: value,
              }))
            }
            placeholder="기부용도를 선택해주세요"
            className="mb-24 w-full"
          />
        ) : (
          <Skeleton />
        )}
      </InputDecorator>
      <div className="flex flex-col">
        <div className="mb-20 flex items-center justify-between border-y-1 border-Gray-300 py-20">
          <span
            className={cn(
              'flex gap-8 font-500 text-Gray-800 typo-body3',
              'mobile-tablet:typo-body4',
            )}
          >
            기부 금액
          </span>
          <span className={cn('font-700 typo-h6', 'mobile-tablet:typo-body1')}>
            <CountUp preserveValue end={donation.amount} duration={0.3} />원
          </span>
        </div>
        <AnimatePresence>
          {donation.amount < MIN_DONATION_AMOUNT && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{
                opacity: 1,
                scale: 1,
                transition: { duration: 0.3, ease: 'easeOut' },
              }}
              exit={{
                opacity: 0,
                scale: 0.95,
                transition: { duration: 0.2, ease: 'easeIn' },
              }}
              className="flex flex-col gap-8"
            >
              <p className="text-center font-400 text-R-500 typo-body4">
                {MIN_DONATION_AMOUNT.toLocaleString()}원 이상부터 기부 가능해요.
              </p>
            </motion.div>
          )}
        </AnimatePresence>

        <p
          className={cn(
            'mb-16 text-start text-Gray-700 typo-body3',
            'mobile-tablet:mb-16 mobile-tablet:typo-body4',
          )}
        >
          이용약관에 동의해주세요.
        </p>
        <div className="mb-16 rounded-xsmall bg-Gray-100 px-12 py-8">
          <Checkbox
            className="font-600"
            checked={isAgreementAll}
            onCheckedChange={toggleAllAgreements}
          >
            전체 동의
          </Checkbox>
        </div>
        <div className="mb-32 flex flex-col">
          <Checkbox
            checked={agreements.welloTerms}
            onCheckedChange={(checked) =>
              updateAgreement('welloTerms', checked)
            }
            className="justify-between"
          >
            <div className="flex flex-1 items-start justify-between gap-4">
              <div className="flex items-start gap-4">
                <span className="font-500 text-Gray-black typo-body4">
                  (필수)
                </span>
                <span className="font-400 text-Gray-800 typo-body4">
                  웰로 고향사랑기부제 이용약관
                </span>
              </div>
              <button
                className="font-600 underline typo-body4"
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleTermsView(TERM_CATEGORY.GOHYANG_LOVE_DONATION_TERMS);
                }}
              >
                보기
              </button>
            </div>
          </Checkbox>

          <Checkbox
            checked={agreements.privacyCollection}
            onCheckedChange={(checked) =>
              updateAgreement('privacyCollection', checked)
            }
            className="justify-between"
          >
            <div className="flex flex-1 items-start justify-between gap-4">
              <div className="flex items-start gap-4">
                <span className="font-500 text-Gray-black typo-body4">
                  (필수)
                </span>
                <span className="font-400 text-Gray-800 typo-body4">
                  개인정보 수집이용 동의
                </span>
              </div>
              <button
                className="font-600 underline typo-body4"
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleTermsView(
                    TERM_CATEGORY.PERSONAL_INFO_COLLECTION_AGREEMENT,
                  );
                }}
              >
                보기
              </button>
            </div>
          </Checkbox>

          <div className="flex flex-col gap-8">
            <Checkbox
              checked={isThirdPartyAllChecked}
              onCheckedChange={(checked) =>
                updateAgreement('thirdPartyInfo', checked)
              }
              className="justify-between"
            >
              <div className="flex flex-1 items-start justify-between gap-4">
                <div className="flex items-start gap-4">
                  <span className="font-500 text-Gray-black typo-body4">
                    (필수)
                  </span>
                  <span
                    className="flex cursor-pointer items-start gap-4 font-400 text-Gray-800 typo-body4"
                    onClick={(e) => {
                      e.preventDefault();
                      toggleThirdPartyAccordion();
                    }}
                  >
                    <span className="text-start">
                      고향사랑e음 회원가입 및 연계를 위한 개인정보 제3자 제공
                      동의
                    </span>
                    <SvgChevrondown
                      className={cn(
                        'size-16 min-h-21 min-w-16 transition-transform duration-200',
                        {
                          'rotate-180': isThirdPartyExpanded,
                        },
                      )}
                    />
                  </span>
                </div>
              </div>
            </Checkbox>

            {isThirdPartyExpanded && (
              <div className="flex flex-col pl-16">
                <Checkbox
                  checked={agreements.membershipTerms}
                  onCheckedChange={(checked) =>
                    updateAgreement('membershipTerms', checked)
                  }
                  className="justify-between"
                >
                  <div className="flex flex-1 items-start justify-between gap-4">
                    <span className="font-400 text-Gray-800 typo-body4">
                      고향사랑e음 회원가입을 위한 개인정보 제공 동의
                    </span>
                    <button
                      className="font-600 underline typo-body4"
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleTermsView(
                          TERM_CATEGORY.GOHYANG_EUM_SIGNUP_THIRD_PARTY_AGREEMENT,
                        );
                      }}
                    >
                      보기
                    </button>
                  </div>
                </Checkbox>

                <Checkbox
                  checked={agreements.addressVerification}
                  onCheckedChange={(checked) =>
                    updateAgreement('addressVerification', checked)
                  }
                  className="justify-between"
                >
                  <div className="flex flex-1 items-start justify-between gap-4">
                    <span className="font-400 text-Gray-800 typo-body4">
                      주소지 확인을 위한 주민등록번호 제공 동의
                    </span>
                    <button
                      className="font-600 underline typo-body4"
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleTermsView(
                          TERM_CATEGORY.RESIDENT_REGISTRATION_PROVIDE_AGREEMENT,
                        );
                      }}
                    >
                      보기
                    </button>
                  </div>
                </Checkbox>

                <Checkbox
                  checked={agreements.shippingInfo}
                  onCheckedChange={(checked) =>
                    updateAgreement('shippingInfo', checked)
                  }
                  className="justify-between"
                >
                  <div className="flex flex-1 items-start justify-between gap-4">
                    <span className="font-400 text-Gray-800 typo-body4">
                      답례품 배송 관련 개인정보 제3자 제공 동의
                    </span>
                    <button
                      className="font-600 underline typo-body4"
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleTermsView(
                          TERM_CATEGORY.GIFT_DELIVERY_THIRD_PARTY_AGREEMENT,
                        );
                      }}
                    >
                      보기
                    </button>
                  </div>
                </Checkbox>
              </div>
            )}
          </div>

          <Checkbox
            checked={agreements.voluntaryDonation}
            onCheckedChange={(checked) =>
              updateAgreement('voluntaryDonation', checked)
            }
            className="justify-between"
          >
            <div className="flex flex-1 items-start justify-between gap-4">
              <span className="flex gap-4">
                <span className="font-500 text-Gray-black typo-body4">
                  (필수)
                </span>
                <span className="font-400 text-Gray-800 typo-body4">
                  공무 관련 강요 없이 자발적으로 기부합니다.
                </span>
              </span>
              <button
                className="font-600 underline typo-body4"
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleTermsView(TERM_CATEGORY.TERMS_GOVERNMENT_PRESSURE);
                }}
              >
                보기
              </button>
            </div>
          </Checkbox>

          <Checkbox
            checked={agreements.refundPolicy}
            onCheckedChange={(checked) =>
              updateAgreement('refundPolicy', checked)
            }
            className="justify-between"
          >
            <div className="flex flex-1 items-start justify-between gap-4">
              <span className="flex gap-4">
                <span className="font-500 text-Gray-black typo-body4">
                  (필수)
                </span>
                <span className="font-400 text-Gray-800 typo-body4">
                  안내사항(환불 불가·세액공제 등)을 확인했습니다.
                </span>
              </span>
              <button
                className="font-600 underline typo-body4"
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleTermsView(TERM_CATEGORY.TERMS_GUIDANCE_NOTICE);
                }}
              >
                보기
              </button>
            </div>
          </Checkbox>

          <Checkbox
            checked={agreements.giftInfo}
            onCheckedChange={(checked) => updateAgreement('giftInfo', checked)}
            className="justify-between"
          >
            <div className="flex flex-1 items-start justify-between gap-4">
              <span className="flex gap-4">
                <span className="font-500 text-Gray-black typo-body4">
                  (필수)
                </span>
                <span className="font-400 text-Gray-800 typo-body4">
                  답례품 정보 확인 및 동의
                </span>
              </span>
              <button
                className="font-600 underline typo-body4"
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleTermsView(
                    TERM_CATEGORY.TERMS_GIFT_INFO_CHECK_AGREEMENT,
                  );
                }}
              >
                보기
              </button>
            </div>
          </Checkbox>
        </div>
      </div>
      <footer className="flex gap-8">
        <Button theme="sub-line" onClick={handleReset}>
          <SvgRefresh className="size-16" />
          초기화
        </Button>
        <Button
          disabled={
            !isAgreementAll ||
            !donation.amount ||
            !donation.purpose ||
            donation.amount < MIN_DONATION_AMOUNT
          }
          onClick={() => onSubmit(donation)}
          className="grow"
        >
          기부하기
        </Button>
      </footer>
    </div>
  );
};
